import { useState, useEffect } from "react";
import { useSchedule } from "@/hooks/useSchedule";

// Returns a live countdown tied to TODAY's truck stop (US Eastern time) only.
//   before open  -> { phase: "opens",  clock }
//   during hours -> { phase: "closes", clock }
//   otherwise (no stop today / already closed) -> null
function etNowParts(date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  return parts.reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
}

export function useEventCountdown() {
  const locations = useSchedule();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const p = etNowParts(new Date(now));
  const isoToday = `${p.year}-${p.month}-${p.day}`;
  let hh = parseInt(p.hour, 10);
  if (hh === 24) hh = 0;
  const nowSec = hh * 3600 + parseInt(p.minute, 10) * 60 + parseInt(p.second, 10);

  const stop = locations.find((l) => l.iso === isoToday && l.start && l.end);
  if (!stop) return null;

  const toSec = (s) => {
    const [h, m] = s.split(":").map(Number);
    return h * 3600 + m * 60;
  };
  const startSec = toSec(stop.start);
  const endSec = toSec(stop.end);

  let remaining;
  let phase;
  if (nowSec < startSec) {
    remaining = startSec - nowSec;
    phase = "opens";
  } else if (nowSec < endSec) {
    remaining = endSec - nowSec;
    phase = "closes";
  } else {
    return null;
  }

  const hrs = Math.floor(remaining / 3600);
  const mins = Math.floor((remaining % 3600) / 60);
  const secs = remaining % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const clock = hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${mins}:${pad(secs)}`;

  return { phase, clock, place: stop.place };
}
